import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { baseUrl } from '@fs/zion-config'
import { SocialCall, HelpAskQuestion } from '@fs/zion-icon'
import { i18n } from '@fs/zion-locale'
import { Link } from '@fs/zion-router'
import { Button, Card, DialogOverlay, Paragraph, Separator, useOverlay } from '@fs/zion-ui'
import { Trans, useTranslation } from 'react-i18next'

import BaseOfferCard from './BaseOfferCard'
import Messenger from '../../../icons/Messenger'
import WhatsApp from '../../../icons/WhatsApp'
import { ColorContext } from '../ColorProvider'

const overlayViews = [
  {
    title: i18n.t('apac.nlihp.offers.help-card.options-title', 'How can we help?'),
    component: HelpOptionsContent,
  },
  {
    title: i18n.t('apac.nlihp.offers.help-card.account-help-title', 'Account Help'),
    component: AccountHelpContent,
  },
  {
    title: i18n.t('apac.nlihp.offers.help-card.church-account-recovery-title', 'Church Account Recovery Phone'),
    component: ChurchAccountRecoveryContent,
  },
]

export default function HelpCard({ simple }) {
  const { t } = useTranslation()
  const overlay = useOverlay()

  return (
    <>
      {!simple && <HelpOverlay overlay={overlay} />}
      <BaseOfferCard
        data-testid="help-card"
        title={t('apac.nlihp.offers.help-card.heading', 'Help and Learning')}
        description={t(
          'apac.nlihp.offers.help-card.subheading',
          'Get assistance with your family history or logging in.'
        )}
        Icon={HelpAskQuestion}
        to={simple ? `${baseUrl}/help/helpcenter/landing` : undefined}
        external={simple}
        onClick={simple ? undefined : overlay.handleClick}
        linkName="Button: Help Card: Click"
      />
    </>
  )
}

function HelpOverlay({ overlay }) {
  const [currentView, setCurrentView] = useState(overlayViews[0])
  const View = currentView.component
  const { isOpen } = overlay

  useEffect(() => {
    if (isOpen) {
      setCurrentView(overlayViews[0])
    }
  }, [isOpen])

  return (
    <DialogOverlay fullscreen={false} title={currentView.title} {...overlay} size="md">
      <View setCurrentView={setCurrentView} />
      <Separator size="xxs" />
    </DialogOverlay>
  )
}

function OptionCard({ text, onClick, to, external, linkName, testid }) {
  const { backgroundColor } = useContext(ColorContext)

  return (
    <Card
      data-testid={testid}
      edges="none"
      margins="xs"
      backgroundColor={backgroundColor}
      onClick={onClick}
      to={to}
      external={external}
      linkName={linkName}
    >
      <Paragraph centered size="sm">
        {text}
      </Paragraph>
    </Card>
  )
}

function HelpOptionsContent({ setCurrentView }) {
  const { t } = useTranslation()
  // Help Center doesn't have a localized page for Fijian, so redirect to English version
  const helpCenterPath = `${baseUrl}${i18n.language === 'fj' ? '/en' : ''}/help/helpcenter/landing`
  return (
    <>
      <OptionCard
        testid="help-card-family-history"
        text={t('apac.nlihp.offers.help-card.family-history', 'I need help with my family history')}
        external
        to={helpCenterPath}
        linkName="button: help-card: family-history: click"
      />
      <Separator size="xxs" />
      <OptionCard
        testid="help-card-resources"
        text={t('apac.nlihp.offers.help-card.resources', "I'm looking for resources")}
        external
        to="/wiki/Pacific_Island_Guide_to_Family_History_Research"
        linkName="button: help-card: resources: click"
      />
      <Separator size="xxs" />
      <OptionCard
        testid="help-card-cant-login"
        text={t('apac.nlihp.offers.help-card.cant-login', "I can't login to my account")}
        onClick={() => setCurrentView(overlayViews[1])}
        linkName="button: help-card: cant-login: click"
      />
    </>
  )
}

function AccountHelpContent({ setCurrentView }) {
  const { t } = useTranslation()
  return (
    <>
      <Button
        emphasis="low"
        Icon={WhatsApp}
        to="https://wa.me/+13858992500"
        external
        linkName="button: help-card: whatsapp: click"
      >
        {t('apac.social.whatsapp', 'WhatsApp')}
      </Button>
      <Button
        emphasis="low"
        Icon={Messenger}
        to="https://m.me/111094955337463"
        external
        linkName="button: help-card: messenger: click"
      >
        {t('apac.social.messenger', 'Messenger')}
      </Button>
      <Button
        emphasis="low"
        Icon={SocialCall}
        onClick={() => setCurrentView(overlayViews[2])}
        linkName="button: help-card: church-account-recovery: click"
      >
        {t('apac.social.church-account-recovery', 'Church Account Recovery')}
      </Button>
    </>
  )
}

const listCss = css`
  padding-inline-start: 12px;
  margin: 12px 0;
  li {
    padding-bottom: 6px;
  }
`

function ChurchAccountRecoveryContent() {
  const { t } = useTranslation()

  return (
    <>
      <Paragraph size="sm">
        <Trans
          i18nKey="apac.nlihp.help-card.america-samoa"
          components={[
            <Link
              wayfinding
              to="tel:18002043938"
              external
              linkName="link: help-card: america-samoa-phone: click"
              data-testid="link-help-card-america-samoa-phone"
            />,
          ]}
        >
          {'<strong>America Samoa:</strong> <0>1800 2043 938</0> (Toll Free)'}
        </Trans>
      </Paragraph>

      <Paragraph size="sm">
        <Trans
          i18nKey="apac.nlihp.help-card.fiji"
          components={[
            <Link
              wayfinding
              to="tel:8002670"
              external
              linkName="link: help-card: fiji-phone: click"
              data-testid="link-help-card-fiji-phone"
            />,
          ]}
        >
          {'<strong>Fiji:</strong> <0>800 2670</0>'}
        </Trans>
      </Paragraph>

      <Paragraph size="sm">
        <Trans
          i18nKey="apac.nlihp.help-card.new-caledonia"
          components={[
            <Link
              wayfinding
              to="tel:18013534835"
              external
              linkName="link: help-card: new-caledonia-phone: click"
              data-testid="link-help-card-new-caledonia-phone"
            />,
          ]}
        >
          {'<strong>New Caledonia:</strong> <0>1801 353 4835</0> (Toll Free)'}
        </Trans>
      </Paragraph>

      <Paragraph size="sm">
        <strong>{t('apac.nlihp.help-card.samoa-header', 'Samoa:')}</strong>
      </Paragraph>
      <ul css={listCss}>
        <li>
          <Trans
            i18nKey="apac.nlihp.help-card.samoa-blue-sky"
            components={[
              <Link
                wayfinding
                to="tel:800472"
                external
                linkName="link: help-card: samoa-blue-sky-phone: click"
                data-testid="link-help-card-samoa-blue-sky-phone"
              />,
            ]}
          >
            {'<0>800472</0> (Toll free for Blue Sky)'}
          </Trans>
        </li>
        <li>
          <Trans
            i18nKey="apac.nlihp.help-card.samoa-others"
            components={[
              <Link
                wayfinding
                to="tel:64226"
                external
                linkName="link: help-card: samoa-others-phone: click"
                data-testid="link-help-card-samoa-others-phone"
              />,
            ]}
          >
            {'<0>64226</0> (Toll free for all others)'}
          </Trans>
        </li>
      </ul>

      <Paragraph size="sm">
        <Trans
          i18nKey="apac.nlihp.help-card.french-polynesia"
          components={[
            <Link
              wayfinding
              to="tel:+68940505507"
              external
              linkName="link: help-card: french-polynesia-phone: click"
              data-testid="link-help-card-french-polynesia-phone"
            />,
          ]}
        >
          {'<strong>French Polynesia (Tahiti):</strong> <0>+689 40 50 55 07</0>'}
        </Trans>
      </Paragraph>

      <Paragraph size="sm">
        <strong>{t('apac.nlihp.help-card.tonga-header', 'Tonga:')}</strong>
      </Paragraph>

      <ul css={listCss}>
        <li>
          <Trans
            i18nKey="apac.nlihp.help-card.tonga-tcc"
            components={[
              <Link
                wayfinding
                to="tel:0800437"
                external
                linkName="link: help-card: tonga-tcc-phone: click"
                data-testid="link-help-card-tonga-tcc-phone"
              />,
            ]}
          >
            {'<0>0800 437</0> (TCC)'}
          </Trans>
        </li>
        <li>
          <Trans
            i18nKey="apac.nlihp.help-card.tonga-non-tcc"
            components={[
              <Link
                wayfinding
                to="tel:20360"
                external
                linkName="link: help-card: tonga-non-tcc-phone: click"
                data-testid="link-help-card-tonga-non-tcc-phone"
              />,
            ]}
          >
            {'<0>20360</0> (Non-TCC)'}
          </Trans>
        </li>
      </ul>

      <Paragraph size="sm">
        <Trans
          i18nKey="apac.nlihp.help-card.tahiti"
          components={[
            <Link
              wayfinding
              to="tel:505507"
              external
              linkName="link: help-card: tahiti-phone: click"
              data-testid="link-help-card-tahiti-phone"
            />,
          ]}
        >
          {'<strong>Tahiti:</strong> <0>505 507</0> (Toll Free)'}
        </Trans>
      </Paragraph>
    </>
  )
}
